import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle, XCircle, Loader2, Send, Users, Shield, Plus, Trash2, Mail, Gauge, Zap, Timer, BatteryLow } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { loadConfig } from "@/services/PriceService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Settings = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [testingTelegram, setTestingTelegram] = useState(false);
  const [telegramStatus, setTelegramStatus] = useState(null);
  
  // Whitelist state
  const [whitelist, setWhitelist] = useState([]);
  const [loadingWhitelist, setLoadingWhitelist] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [addingEmail, setAddingEmail] = useState(false);
  const [removingEmail, setRemovingEmail] = useState(null);

  // Price config state
  const [priceConfig, setPriceConfig] = useState(null);
  const [loadingPriceConfig, setLoadingPriceConfig] = useState(true);
  const [applyingPreset, setApplyingPreset] = useState(null);

  // Rollover-cutoff state (per account, per classification)
  const [rolloverAccounts, setRolloverAccounts] = useState([]);
  const [rolloverCutoffs, setRolloverCutoffs] = useState({});   // { [account_id]: { NSE_STOCK:{h,m}, ... } }
  const [rolloverDefaults, setRolloverDefaults] = useState({});
  const [loadingRollover, setLoadingRollover] = useState(true);
  const [savingRollover, setSavingRollover] = useState(null);   // account_id currently being saved

  // Fetch whitelist on mount
  useEffect(() => {
    fetchWhitelist();
    fetchPriceConfig();
    fetchRolloverCutoffs();
  }, []);

  const fetchRolloverCutoffs = async () => {
    try {
      const acctRes = await api.get("/accounts");
      const accts = (acctRes.data || []).filter((a) => a?.speedbot_credentials?.email);
      setRolloverAccounts(accts);
      const results = await Promise.all(
        accts.map(async (a) => {
          const r = await api.get(`/speedbot-api/rollover-cutoffs/${a.id}`);
          return { id: a.id, data: r.data };
        })
      );
      const byId = {};
      let defaults = {};
      for (const { id, data } of results) {
        byId[id] = data.cutoffs;
        defaults = data.defaults || defaults;
      }
      setRolloverCutoffs(byId);
      setRolloverDefaults(defaults);
    } catch (err) {
      console.error("Failed to load rollover cutoffs:", err);
    } finally {
      setLoadingRollover(false);
    }
  };

  const updateCutoff = (accountId, cls, field, value) => {
    setRolloverCutoffs((prev) => ({
      ...prev,
      [accountId]: {
        ...prev[accountId],
        [cls]: {
          ...prev[accountId]?.[cls],
          [field]: Number(value),
        },
      },
    }));
  };

  const saveRolloverCutoffsFor = async (accountId) => {
    setSavingRollover(accountId);
    try {
      const payload = rolloverCutoffs[accountId] || {};
      await api.put(`/speedbot-api/rollover-cutoffs/${accountId}`, payload);
      toast.success("Rollover cut-offs saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save cut-offs");
    } finally {
      setSavingRollover(null);
    }
  };

  const fetchPriceConfig = async () => {
    try {
      const response = await api.get("/price-config");
      if (response.data.status) {
        setPriceConfig(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching price config:", error);
    } finally {
      setLoadingPriceConfig(false);
    }
  };

  const applyPreset = async (presetName) => {
    setApplyingPreset(presetName);
    try {
      const response = await api.post(`/price-config/preset/${presetName}`);
      if (response.data.status) {
        setPriceConfig(prev => ({ ...prev, ...response.data.data }));
        toast.success(`Applied "${presetName}" preset - Refresh interval: ${response.data.data.frontend_refresh_interval_ms / 1000}s`);
        // Reload frontend config
        await loadConfig();
      }
    } catch (error) {
      toast.error("Failed to apply preset");
    } finally {
      setApplyingPreset(null);
    }
  };

  const fetchWhitelist = async () => {
    try {
      const response = await api.get("/auth/whitelist", {
        withCredentials: true
      });
      setWhitelist(response.data.emails || []);
    } catch (error) {
      console.error("Error fetching whitelist:", error);
      toast.error("Failed to load whitelist");
    } finally {
      setLoadingWhitelist(false);
    }
  };

  const handleAddEmail = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setAddingEmail(true);
    try {
      await api.post("/auth/whitelist", 
        { email: newEmail.trim() },
        { withCredentials: true }
      );
      toast.success(`Added ${newEmail} to whitelist`);
      setNewEmail("");
      fetchWhitelist();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to add email";
      toast.error(message);
    } finally {
      setAddingEmail(false);
    }
  };

  const handleRemoveEmail = async (email) => {
    if (email.toLowerCase() === user?.email?.toLowerCase()) {
      toast.error("You cannot remove your own email");
      return;
    }

    setRemovingEmail(email);
    try {
      await api.delete(`/auth/whitelist/${encodeURIComponent(email)}`, {
        withCredentials: true
      });
      toast.success(`Removed ${email} from whitelist`);
      fetchWhitelist();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to remove email";
      toast.error(message);
    } finally {
      setRemovingEmail(null);
    }
  };

  const handleTestTelegram = async () => {
    setTestingTelegram(true);
    setTelegramStatus(null);
    try {
      const response = await api.post("/test-telegram");
      if (response.data.status) {
        setTelegramStatus('connected');
        toast.success("Telegram test message sent! Check your Telegram.");
      } else {
        setTelegramStatus('failed');
        toast.error(`Telegram failed: ${response.data.message}`);
      }
    } catch (error) {
      setTelegramStatus('failed');
      toast.error("Telegram test failed");
    } finally {
      setTestingTelegram(false);
    }
  };

  return (
    <div data-testid="settings-page">
      <button
        onClick={() => navigate("/dashboard")}
        className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Dashboard
      </button>

      <div className="mb-8">
        <h1 className="font-heading font-semibold tracking-tight text-4xl sm:text-5xl text-white mb-2">
          Settings
        </h1>
        <p className="text-slate-400 text-base">Manage your broker accounts, access control, and integrations</p>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Current User Card */}
        {user && (
          <div className="bg-charcoal/50 border border-white/10 rounded-md p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {user.picture && (
                  <img 
                    src={user.picture} 
                    alt={user.name} 
                    className="w-12 h-12 rounded-full"
                  />
                )}
                <div>
                  <h2 className="font-heading font-semibold text-lg text-white">{user.name}</h2>
                  <p className="text-slate-400 text-sm">{user.email}</p>
                </div>
              </div>
              <Button
                onClick={logout}
                variant="outline"
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              >
                Sign Out
              </Button>
            </div>
          </div>
        )}

        {/* Access Control / Email Whitelist */}
        <div className="bg-charcoal/50 border border-white/10 rounded-md p-6">
          <div className="mb-6">
            <h2 className="font-heading font-semibold tracking-tight text-xl text-white mb-2 flex items-center gap-2">
              <Shield className="w-5 h-5 text-yellow-400" />
              Access Control
            </h2>
            <p className="text-slate-400 text-sm">Manage which email addresses can access this application</p>
          </div>

          {/* Add Email Form */}
          <form onSubmit={handleAddEmail} className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Enter email to authorize..."
                className="w-full bg-gray-800 border border-gray-700 rounded-md pl-10 pr-4 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-yellow-400"
              />
            </div>
            <Button
              type="submit"
              disabled={addingEmail || !newEmail.trim()}
              className="bg-yellow-400 text-gray-900 hover:bg-yellow-500"
            >
              {addingEmail ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </>
              )}
            </Button>
          </form>

          {/* Whitelist */}
          <div className="space-y-2">
            {loadingWhitelist ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-yellow-400" />
              </div>
            ) : whitelist.length === 0 ? (
              <p className="text-slate-500 text-center py-4">No emails in whitelist</p>
            ) : (
              whitelist.map((email) => (
                <div 
                  key={email}
                  className="flex items-center justify-between bg-gray-800/50 border border-gray-700 rounded-md px-4 py-3"
                >
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-slate-500" />
                    <span className="text-white">{email}</span>
                    {email.toLowerCase() === user?.email?.toLowerCase() && (
                      <span className="text-xs bg-yellow-400/20 text-yellow-400 px-2 py-0.5 rounded">You</span>
                    )}
                  </div>
                  <button
                    onClick={() => handleRemoveEmail(email)}
                    disabled={removingEmail === email || email.toLowerCase() === user?.email?.toLowerCase()}
                    className="text-slate-500 hover:text-red-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title={email.toLowerCase() === user?.email?.toLowerCase() ? "Cannot remove yourself" : "Remove from whitelist"}
                  >
                    {removingEmail === email ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              ))
            )}
          </div>

          <p className="text-xs text-slate-500 mt-4">
            Only users with whitelisted email addresses can sign in to this application.
          </p>
        </div>

        {/* Account Management Card */}
        <div className="bg-charcoal/50 border border-white/10 rounded-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading font-semibold tracking-tight text-xl text-white mb-2 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-400" />
                Trading Accounts
              </h2>
              <p className="text-slate-400 text-sm">Manage your broker accounts and API credentials for each account</p>
            </div>
            <Button
              onClick={() => navigate("/accounts")}
              className="bg-blue-600 text-white hover:bg-blue-700"
              data-testid="manage-accounts-button"
            >
              Manage Accounts
            </Button>
          </div>
        </div>

        {/* Price Refresh Configuration */}
        <div className="bg-charcoal/50 border border-white/10 rounded-md p-6 space-y-6">
          <div>
            <h2 className="font-heading font-semibold tracking-tight text-xl text-white mb-2 flex items-center gap-2">
              <Gauge className="w-5 h-5 text-green-400" />
              Price Refresh Speed
            </h2>
            <p className="text-slate-400 text-sm">Configure how frequently prices are updated (no redeployment needed)</p>
          </div>

          {loadingPriceConfig ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-green-400" />
            </div>
          ) : (
            <>
              {/* Current Settings */}
              {priceConfig && (
                <div className="bg-gray-800/50 border border-gray-700 rounded-md p-4">
                  <h3 className="text-white font-medium mb-3 text-sm">Current Settings</h3>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Refresh Interval</p>
                      <p className="text-white font-mono">{priceConfig.frontend_refresh_interval_ms / 1000}s</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Cache Duration</p>
                      <p className="text-white font-mono">{priceConfig.frontend_cache_duration_ms / 1000}s</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Backend Cache</p>
                      <p className="text-white font-mono">{priceConfig.backend_cache_ttl_seconds}s</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Preset Buttons */}
              <div className="space-y-3">
                <h3 className="text-white font-medium text-sm">Quick Presets</h3>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => applyPreset("fast")}
                    disabled={applyingPreset !== null}
                    className={`flex items-center gap-3 p-4 rounded-md border transition-all ${
                      priceConfig?.frontend_refresh_interval_ms === 5000
                        ? "bg-green-500/20 border-green-500/50 text-green-400"
                        : "bg-gray-800/50 border-gray-700 text-white hover:border-green-500/50"
                    }`}
                  >
                    {applyingPreset === "fast" ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Zap className="w-5 h-5 text-green-400" />
                    )}
                    <div className="text-left">
                      <p className="font-medium">Fast</p>
                      <p className="text-xs text-slate-400">5s refresh • Real-time trading</p>
                    </div>
                  </button>

                  <button
                    onClick={() => applyPreset("normal")}
                    disabled={applyingPreset !== null}
                    className={`flex items-center gap-3 p-4 rounded-md border transition-all ${
                      priceConfig?.frontend_refresh_interval_ms === 15000
                        ? "bg-blue-500/20 border-blue-500/50 text-blue-400"
                        : "bg-gray-800/50 border-gray-700 text-white hover:border-blue-500/50"
                    }`}
                  >
                    {applyingPreset === "normal" ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Timer className="w-5 h-5 text-blue-400" />
                    )}
                    <div className="text-left">
                      <p className="font-medium">Normal</p>
                      <p className="text-xs text-slate-400">15s refresh • Balanced</p>
                    </div>
                  </button>

                  <button
                    onClick={() => applyPreset("slow")}
                    disabled={applyingPreset !== null}
                    className={`flex items-center gap-3 p-4 rounded-md border transition-all ${
                      priceConfig?.frontend_refresh_interval_ms === 30000
                        ? "bg-yellow-500/20 border-yellow-500/50 text-yellow-400"
                        : "bg-gray-800/50 border-gray-700 text-white hover:border-yellow-500/50"
                    }`}
                  >
                    {applyingPreset === "slow" ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Gauge className="w-5 h-5 text-yellow-400" />
                    )}
                    <div className="text-left">
                      <p className="font-medium">Slow</p>
                      <p className="text-xs text-slate-400">30s refresh • Light usage</p>
                    </div>
                  </button>

                  <button
                    onClick={() => applyPreset("battery_saver")}
                    disabled={applyingPreset !== null}
                    className={`flex items-center gap-3 p-4 rounded-md border transition-all ${
                      priceConfig?.frontend_refresh_interval_ms === 60000
                        ? "bg-orange-500/20 border-orange-500/50 text-orange-400"
                        : "bg-gray-800/50 border-gray-700 text-white hover:border-orange-500/50"
                    }`}
                  >
                    {applyingPreset === "battery_saver" ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <BatteryLow className="w-5 h-5 text-orange-400" />
                    )}
                    <div className="text-left">
                      <p className="font-medium">Battery Saver</p>
                      <p className="text-xs text-slate-400">60s refresh • Minimal API calls</p>
                    </div>
                  </button>
                </div>
              </div>

              <p className="text-xs text-slate-500">
                💡 Faster refresh = more API calls. Use "Fast" during active trading, "Battery Saver" for monitoring.
              </p>
            </>
          )}
        </div>

        {/* Rollover Cut-off Times — per account, per exchange class */}
        <div
          data-testid="rollover-cutoffs-section"
          className="bg-charcoal/50 border border-white/10 rounded-md p-6 space-y-6"
        >
          <div>
            <h2 className="font-heading font-semibold tracking-tight text-xl text-white mb-2">
              Contract Rollover Cut-offs
            </h2>
            <p className="text-slate-400 text-sm">
              After this time (IST) on the calculated rollover trading day, if the position
              hasn&apos;t rolled over yet, we flag it in the Open Trades table and send a Telegram alert.
              Default: NSE 14:10 · MCX 18:10.
            </p>
          </div>

          {loadingRollover ? (
            <div className="text-slate-400 text-sm">Loading…</div>
          ) : rolloverAccounts.length === 0 ? (
            <div className="text-slate-400 text-sm">
              No SpeedBot-linked accounts yet. Add one in Accounts to configure cut-offs.
            </div>
          ) : (
            <div className="space-y-4">
              {rolloverAccounts.map((acct) => {
                const cuts = rolloverCutoffs[acct.id] || {};
                return (
                  <div
                    key={acct.id}
                    data-testid={`rollover-cutoff-card-${acct.id}`}
                    className="border border-white/10 rounded-md p-4 bg-black/20"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="text-white font-medium text-base">{acct.name}</h3>
                        <p className="text-slate-500 text-xs mt-0.5">
                          {acct.speedbot_credentials?.email}
                        </p>
                      </div>
                      <button
                        data-testid={`save-cutoffs-${acct.id}`}
                        onClick={() => saveRolloverCutoffsFor(acct.id)}
                        disabled={savingRollover === acct.id}
                        className="px-4 py-1.5 rounded-sm text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                      >
                        {savingRollover === acct.id ? "Saving…" : "Save"}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {[
                        { key: "NSE_STOCK", label: "NSE Stocks", hint: "T-1 trading day" },
                        { key: "NSE_INDEX", label: "NSE Indices", hint: "T-0 (expiry day)" },
                        { key: "MCX",       label: "MCX Commodities", hint: "T-5 trading days" },
                      ].map(({ key, label, hint }) => {
                        const v = cuts[key] || rolloverDefaults[key] || { hour: 14, minute: 10 };
                        return (
                          <div
                            key={key}
                            data-testid={`rollover-cutoff-${acct.id}-${key}`}
                            className="p-3 rounded bg-black/40 border border-white/5"
                          >
                            <div className="text-white text-sm font-medium">{label}</div>
                            <div className="text-slate-500 text-[10px] mb-2">{hint}</div>
                            <div className="flex items-center gap-1">
                              <input
                                type="number" min={0} max={23}
                                value={v.hour}
                                onChange={(e) => updateCutoff(acct.id, key, "hour", e.target.value)}
                                data-testid={`rollover-hour-${acct.id}-${key}`}
                                className="w-14 bg-black/50 border border-white/10 rounded px-2 py-1 text-white text-sm text-center"
                              />
                              <span className="text-slate-400">:</span>
                              <input
                                type="number" min={0} max={59}
                                value={v.minute}
                                onChange={(e) => updateCutoff(acct.id, key, "minute", e.target.value)}
                                data-testid={`rollover-minute-${acct.id}-${key}`}
                                className="w-14 bg-black/50 border border-white/10 rounded px-2 py-1 text-white text-sm text-center"
                              />
                              <span className="text-slate-500 text-xs ml-1">IST</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Telegram Alerts Section */}
        <div className="bg-charcoal/50 border border-white/10 rounded-md p-6 space-y-6">
          <div>
            <h2 className="font-heading font-semibold tracking-tight text-xl text-white mb-2">
              Telegram Alerts
            </h2>
            <p className="text-slate-400 text-sm">Test your Telegram notification connection</p>
          </div>

          {/* Telegram Status */}
          {telegramStatus && (
            <div className={`p-4 rounded-md flex items-center gap-3 ${
              telegramStatus === 'connected' 
                ? 'bg-profit/10 border border-profit/30' 
                : 'bg-loss/10 border border-loss/30'
            }`}>
              {telegramStatus === 'connected' ? (
                <>
                  <CheckCircle className="w-5 h-5 text-profit" />
                  <div>
                    <p className="text-white font-medium">Telegram Connected</p>
                    <p className="text-sm text-slate-300">Check your Telegram for a test message</p>
                  </div>
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5 text-loss" />
                  <div>
                    <p className="text-white font-medium">Telegram Failed</p>
                    <p className="text-sm text-slate-300">Please check bot token and chat ID</p>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="bg-blue-900/20 border border-blue-900/50 rounded-md p-4">
            <h3 className="text-white font-medium mb-2 text-sm">You will receive alerts for:</h3>
            <ul className="text-xs text-slate-300 space-y-1 list-disc list-inside">
              <li>Stop Loss hit notifications</li>
              <li>Target reached notifications</li>
              <li>New entry signal alerts</li>
              <li>Excel upload confirmations</li>
            </ul>
          </div>

          <Button
            onClick={handleTestTelegram}
            disabled={testingTelegram}
            data-testid="test-telegram-button"
            className="bg-blue-600 text-white hover:bg-blue-700 rounded-sm font-medium px-8 py-3 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {testingTelegram ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send Test Message
              </>
            )}
          </Button>
        </div>

        {/* ── Market Calendar & Hours ── */}
        <MarketCalendarSection />

      </div>
    </div>
  );
};

// ── Market Calendar & Hours Management Component ──
const MarketCalendarSection = () => {
  const [holidays, setHolidays] = useState([]);
  const [marketConfig, setMarketConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddHoliday, setShowAddHoliday] = useState(false);
  const [newHoliday, setNewHoliday] = useState({ date: "", name: "", exchanges: ["NSE", "MCX"], mcx_evening_closed: false });
  const [editingHours, setEditingHours] = useState(null); // "NSE" or "MCX" or null
  const [hoursForm, setHoursForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deletingDate, setDeletingDate] = useState(null);
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [holidaysRes, configRes] = await Promise.all([
        api.get(`/market-holidays?year=${selectedYear}`),
        api.get("/market-config"),
      ]);
      setHolidays(holidaysRes.data.data || []);
      setMarketConfig(configRes.data.data || null);
    } catch (err) {
      console.error("Error loading market data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddHoliday = async (e) => {
    e.preventDefault();
    if (!newHoliday.date || !newHoliday.name) {
      toast.error("Date and name are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/market-holidays", newHoliday);
      toast.success(`Holiday added: ${newHoliday.name}`);
      setNewHoliday({ date: "", name: "", exchanges: ["NSE", "MCX"], mcx_evening_closed: false });
      setShowAddHoliday(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add holiday");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteHoliday = async (date) => {
    setDeletingDate(date);
    try {
      await api.delete(`/market-holidays/${date}`);
      toast.success("Holiday deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete holiday");
    } finally {
      setDeletingDate(null);
    }
  };

  const startEditHours = (exchange) => {
    const cfg = marketConfig?.[exchange] || {};
    setHoursForm({ ...cfg });
    setEditingHours(exchange);
  };

  const handleSaveHours = async () => {
    if (!editingHours) return;
    setSaving(true);
    try {
      await api.put(`/market-config/${editingHours}`, hoursForm);
      toast.success(`${editingHours} hours updated`);
      setEditingHours(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save hours");
    } finally {
      setSaving(false);
    }
  };

  const toggleExchange = (ex) => {
    setNewHoliday(prev => ({
      ...prev,
      exchanges: prev.exchanges.includes(ex)
        ? prev.exchanges.filter(e => e !== ex)
        : [...prev.exchanges, ex]
    }));
  };

  if (loading) {
    return (
      <div className="bg-charcoal/50 border border-white/10 rounded-md p-6">
        <div className="flex items-center gap-2 text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading market calendar...
        </div>
      </div>
    );
  }

  const dstInfo = marketConfig?._dst_info;
  const mcxConfig = marketConfig?.MCX;

  return (
    <div data-testid="market-calendar-section" className="space-y-4">
      {/* Market Hours */}
      <div className="bg-charcoal/50 border border-white/10 rounded-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-semibold text-lg flex items-center gap-2">
            <Timer className="w-5 h-5 text-cyan-400" />
            Market Hours
          </h2>
          {dstInfo && (
            <span className={`text-xs px-2 py-1 rounded ${dstInfo.us_dst_active ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'}`}>
              US DST: {dstInfo.us_dst_active ? "Active" : "Inactive"}
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* NSE Hours */}
          <div className="bg-black/20 rounded-md p-4 border border-white/5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-medium">NSE / NFO</h3>
              <button
                onClick={() => startEditHours("NSE")}
                className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
                data-testid="edit-nse-hours"
              >
                Edit
              </button>
            </div>
            {editingHours === "NSE" ? (
              <div className="space-y-2">
                <div className="flex gap-2 items-center">
                  <label className="text-slate-500 text-xs w-28">Pre-Mkt Open:</label>
                  <input
                    type="time"
                    value={hoursForm.premarket_open || "09:00"}
                    onChange={e => setHoursForm(f => ({ ...f, premarket_open: e.target.value }))}
                    className="bg-black/40 border border-white/10 rounded px-2 py-1 text-sm text-white"
                    data-testid="nse-premarket-open-input"
                  />
                </div>
                <div className="flex gap-2 items-center">
                  <label className="text-slate-500 text-xs w-28">Pre-Mkt Close:</label>
                  <input
                    type="time"
                    value={hoursForm.premarket_close || "09:08"}
                    onChange={e => setHoursForm(f => ({ ...f, premarket_close: e.target.value }))}
                    className="bg-black/40 border border-white/10 rounded px-2 py-1 text-sm text-white"
                    data-testid="nse-premarket-close-input"
                  />
                </div>
                <div className="flex gap-2 items-center">
                  <label className="text-slate-500 text-xs w-28">Open:</label>
                  <input
                    type="time"
                    value={hoursForm.morning_open || "09:15"}
                    onChange={e => setHoursForm(f => ({ ...f, morning_open: e.target.value }))}
                    className="bg-black/40 border border-white/10 rounded px-2 py-1 text-sm text-white"
                  />
                </div>
                <div className="flex gap-2 items-center">
                  <label className="text-slate-500 text-xs w-28">Close:</label>
                  <input
                    type="time"
                    value={hoursForm.morning_close || "15:30"}
                    onChange={e => setHoursForm(f => ({ ...f, morning_close: e.target.value }))}
                    className="bg-black/40 border border-white/10 rounded px-2 py-1 text-sm text-white"
                  />
                </div>
                <p className="text-[10px] text-slate-500">☕ Morning Tea auto-fetches pre-open data 1 min after Pre-Mkt Close.</p>
                <div className="flex gap-2 mt-2">
                  <button onClick={handleSaveHours} disabled={saving} className="px-3 py-1 bg-cyan-600 text-white text-xs rounded hover:bg-cyan-500 disabled:opacity-50">
                    {saving ? "Saving..." : "Save"}
                  </button>
                  <button onClick={() => setEditingHours(null)} className="px-3 py-1 bg-zinc-700 text-white text-xs rounded hover:bg-zinc-600">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-sm space-y-1">
                <p className="text-slate-300" data-testid="nse-premarket-display">
                  <span className="text-slate-500">Pre-Market:</span> {marketConfig?.NSE?.premarket_open || "09:00"} - {marketConfig?.NSE?.premarket_close || "09:08"} IST
                </p>
                <p className="text-slate-300">
                  <span className="text-slate-500">Morning:</span> {marketConfig?.NSE?.morning_open || "09:15"} - {marketConfig?.NSE?.morning_close || "15:30"} IST
                </p>
              </div>
            )}
          </div>

          {/* MCX Hours */}
          <div className="bg-black/20 rounded-md p-4 border border-white/5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-medium">MCX</h3>
              <button
                onClick={() => startEditHours("MCX")}
                className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
                data-testid="edit-mcx-hours"
              >
                Edit
              </button>
            </div>
            {editingHours === "MCX" ? (
              <div className="space-y-2">
                <div className="flex gap-2 items-center">
                  <label className="text-slate-500 text-xs w-20">Morning:</label>
                  <input type="time" value={hoursForm.morning_open || "09:00"} onChange={e => setHoursForm(f => ({ ...f, morning_open: e.target.value }))}
                    className="bg-black/40 border border-white/10 rounded px-2 py-1 text-sm text-white" />
                  <span className="text-slate-600 text-xs">to</span>
                  <input type="time" value={hoursForm.morning_close || "17:00"} onChange={e => setHoursForm(f => ({ ...f, morning_close: e.target.value }))}
                    className="bg-black/40 border border-white/10 rounded px-2 py-1 text-sm text-white" />
                </div>
                <div className="flex gap-2 items-center">
                  <label className="text-slate-500 text-xs w-20">Evening:</label>
                  <input type="time" value={hoursForm.evening_open || "17:00"} onChange={e => setHoursForm(f => ({ ...f, evening_open: e.target.value }))}
                    className="bg-black/40 border border-white/10 rounded px-2 py-1 text-sm text-white" />
                  <span className="text-slate-600 text-xs">to</span>
                  <span className="text-slate-400 text-xs">(auto/DST)</span>
                </div>
                <div className="flex gap-2 items-center">
                  <label className="text-slate-500 text-xs w-20">DST close:</label>
                  <input type="time" value={hoursForm.evening_close_dst || "23:30"} onChange={e => setHoursForm(f => ({ ...f, evening_close_dst: e.target.value }))}
                    className="bg-black/40 border border-white/10 rounded px-2 py-1 text-sm text-white" />
                </div>
                <div className="flex gap-2 items-center">
                  <label className="text-slate-500 text-xs w-20">Non-DST:</label>
                  <input type="time" value={hoursForm.evening_close_no_dst || "23:55"} onChange={e => setHoursForm(f => ({ ...f, evening_close_no_dst: e.target.value }))}
                    className="bg-black/40 border border-white/10 rounded px-2 py-1 text-sm text-white" />
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="auto-dst" checked={hoursForm.auto_detect_dst !== false}
                    onChange={e => setHoursForm(f => ({ ...f, auto_detect_dst: e.target.checked }))}
                    className="accent-cyan-500" />
                  <label htmlFor="auto-dst" className="text-slate-400 text-xs">Auto-detect US DST</label>
                </div>
                <div className="flex gap-2 mt-2">
                  <button onClick={handleSaveHours} disabled={saving} className="px-3 py-1 bg-cyan-600 text-white text-xs rounded hover:bg-cyan-500 disabled:opacity-50">
                    {saving ? "Saving..." : "Save"}
                  </button>
                  <button onClick={() => setEditingHours(null)} className="px-3 py-1 bg-zinc-700 text-white text-xs rounded hover:bg-zinc-600">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-sm space-y-1">
                <p className="text-slate-300">
                  <span className="text-slate-500">Morning:</span> {mcxConfig?.morning_open || "09:00"} - {mcxConfig?.morning_close || "17:00"} IST
                </p>
                <p className="text-slate-300">
                  <span className="text-slate-500">Evening:</span> {mcxConfig?.evening_open || "17:00"} - {mcxConfig?.current_evening_close || "23:30"} IST
                  <span className={`ml-2 text-xs ${mcxConfig?.us_dst_active ? 'text-amber-400' : 'text-blue-400'}`}>
                    ({mcxConfig?.us_dst_active ? "DST" : "Non-DST"})
                  </span>
                </p>
                {mcxConfig?.auto_detect_dst && (
                  <p className="text-slate-600 text-xs">DST auto-detect enabled</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Holiday Calendar */}
      <div className="bg-charcoal/50 border border-white/10 rounded-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-white font-semibold text-lg flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-400" />
              Market Holidays
            </h2>
            <select
              value={selectedYear}
              onChange={e => setSelectedYear(parseInt(e.target.value))}
              className="bg-black/40 border border-white/10 rounded px-2 py-1 text-sm text-white"
            >
              {[currentYear - 1, currentYear, currentYear + 1].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <span className="text-slate-500 text-sm">{holidays.length} holidays</span>
          </div>
          <button
            onClick={() => setShowAddHoliday(!showAddHoliday)}
            className="flex items-center gap-1 text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
            data-testid="add-holiday-btn"
          >
            <Plus className="w-4 h-4" />
            Add Holiday
          </button>
        </div>

        {/* Add Holiday Form */}
        {showAddHoliday && (
          <form onSubmit={handleAddHoliday} className="bg-black/30 rounded-md p-4 mb-4 border border-white/5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div>
                <label className="text-slate-500 text-xs block mb-1">Date</label>
                <input
                  type="date"
                  value={newHoliday.date}
                  onChange={e => setNewHoliday(h => ({ ...h, date: e.target.value }))}
                  className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white"
                  required
                  data-testid="holiday-date-input"
                />
              </div>
              <div>
                <label className="text-slate-500 text-xs block mb-1">Name</label>
                <input
                  type="text"
                  value={newHoliday.name}
                  onChange={e => setNewHoliday(h => ({ ...h, name: e.target.value }))}
                  placeholder="e.g., Republic Day"
                  className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white placeholder-slate-600"
                  required
                  data-testid="holiday-name-input"
                />
              </div>
              <div>
                <label className="text-slate-500 text-xs block mb-1">Exchanges</label>
                <div className="flex items-center gap-3 mt-1">
                  {["NSE", "MCX"].map(ex => (
                    <label key={ex} className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newHoliday.exchanges.includes(ex)}
                        onChange={() => toggleExchange(ex)}
                        className="accent-cyan-500"
                      />
                      <span className="text-slate-300 text-sm">{ex}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newHoliday.mcx_evening_closed}
                  onChange={e => setNewHoliday(h => ({ ...h, mcx_evening_closed: e.target.checked }))}
                  className="accent-amber-500"
                />
                <span className="text-slate-400 text-xs">MCX evening session also closed</span>
              </label>
              <div className="flex-1" />
              <button type="button" onClick={() => setShowAddHoliday(false)} className="px-3 py-1.5 text-slate-400 text-sm hover:text-white">
                Cancel
              </button>
              <button type="submit" disabled={saving} className="px-4 py-1.5 bg-cyan-600 text-white text-sm rounded hover:bg-cyan-500 disabled:opacity-50" data-testid="save-holiday-btn">
                {saving ? "Adding..." : "Add Holiday"}
              </button>
            </div>
          </form>
        )}

        {/* Holiday List */}
        <div className="space-y-1">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs text-slate-600 uppercase tracking-wider border-b border-white/5">
            <div className="col-span-2">Date</div>
            <div className="col-span-1">Day</div>
            <div className="col-span-4">Name</div>
            <div className="col-span-2">Exchanges</div>
            <div className="col-span-2">MCX Evening</div>
            <div className="col-span-1"></div>
          </div>
          {holidays.length === 0 ? (
            <p className="text-slate-500 text-sm py-4 text-center">No holidays for {selectedYear}</p>
          ) : (
            holidays
              .sort((a, b) => a.date.localeCompare(b.date))
              .map(h => {
                const d = new Date(h.date + "T00:00:00");
                const dayName = d.toLocaleDateString("en-US", { weekday: "short" });
                const isPast = new Date(h.date) < new Date(new Date().toISOString().split("T")[0]);
                return (
                  <div
                    key={h.date}
                    className={`grid grid-cols-12 gap-2 px-3 py-2 rounded text-sm ${
                      isPast ? 'text-slate-600' : 'text-slate-300'
                    } hover:bg-white/5 transition-colors`}
                    data-testid={`holiday-row-${h.date}`}
                  >
                    <div className="col-span-2 font-mono text-xs">{h.date}</div>
                    <div className="col-span-1 text-xs">{dayName}</div>
                    <div className="col-span-4">{h.name}</div>
                    <div className="col-span-2">
                      {(h.exchanges || []).map(ex => (
                        <span key={ex} className={`text-xs px-1.5 py-0.5 rounded mr-1 ${
                          ex === "NSE" ? 'bg-blue-500/20 text-blue-400' : 'bg-amber-500/20 text-amber-400'
                        }`}>{ex}</span>
                      ))}
                    </div>
                    <div className="col-span-2">
                      {h.mcx_evening_closed ? (
                        <span className="text-loss text-xs">Closed</span>
                      ) : (
                        <span className="text-profit text-xs">Open</span>
                      )}
                    </div>
                    <div className="col-span-1 text-right">
                      <button
                        onClick={() => handleDeleteHoliday(h.date)}
                        disabled={deletingDate === h.date}
                        className="text-slate-600 hover:text-loss transition-colors disabled:opacity-50"
                        data-testid={`delete-holiday-${h.date}`}
                      >
                        {deletingDate === h.date ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                );
              })
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
